import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useLines } from "@/features/lines/hooks";
import type { BatchCharacteristicResponse } from "@/types/batch-characteristics.types";
import { CharacteristicList } from "./CharacteristicList";
import { CharacteristicEditor } from "./CharacteristicEditor";
import {
  useBatchCharacteristics,
  useDeleteBatchCharacteristic,
  useSaveBatchCharacteristic,
} from "./hooks";
import { useCharacteristicDraft } from "./useCharacteristicDraft";

/**
 * Spec 084 — as características de batelada da organização. Orquestrador: queries e composição,
 * com o rascunho em `useCharacteristicDraft`. A plataforma não traz nenhuma pré-cadastrada.
 */
export function BatchCharacteristicsSection({ canEdit }: { canEdit: boolean }) {
  const query = useBatchCharacteristics();
  const { data: lines = [] } = useLines();
  const save = useSaveBatchCharacteristic();
  const remove = useDeleteBatchCharacteristic();
  const [editing, setEditing] = useState(false);
  const { draft, setDraft, load, reset, problem, payload } = useCharacteristicDraft();

  function fechar() {
    setEditing(false);
    reset();
  }

  async function salvar() {
    try {
      await save.mutateAsync(payload());
      toast.success("Característica salva.");
      fechar();
    } catch (err) {
      toast.error("Não foi possível salvar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  async function apagar(item: BatchCharacteristicResponse) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Apagar "${item.name}"?`,
      description:
        "Ela deixa de ser pedida nas ordens e sai do relatório. As bateladas que já a receberam mantêm o valor.",
      confirmLabel: "Apagar característica",
      typeToConfirm: item.name,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(item.id);
      toast.success("Característica apagada.");
    } catch (err) {
      toast.error("Não foi possível apagar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <Card id="caracteristicas">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base">Características de batelada</CardTitle>
          <p className="text-xs text-muted-foreground">
            Marcadas uma vez na origem e herdadas por tudo o que sai da batelada.
          </p>
        </div>
        {canEdit && !editing ? (
          <Button
            size="sm"
            onClick={() => {
              reset();
              setEditing(true);
            }}
          >
            Nova característica
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isError ? (
          <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : query.isLoading ? (
          <p className="text-[13px] text-text-secondary">Carregando características…</p>
        ) : editing ? (
          <CharacteristicEditor
            draft={draft}
            setDraft={setDraft}
            lines={lines.map((l) => ({ id: l.id, name: l.name }))}
            problem={problem}
            isSaving={save.isPending}
            onSave={() => void salvar()}
            onCancel={fechar}
          />
        ) : (
          <CharacteristicList
            items={query.data ?? []}
            canEdit={canEdit}
            onEdit={(item) => {
              load(item);
              setEditing(true);
            }}
            onDelete={(item) => void apagar(item)}
            onNew={() => {
              reset();
              setEditing(true);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
